<script lang="ts">
import {defineComponent, ref, watch, toRefs, type PropType} from 'vue';
import type {ApiOfferSubArray, RcaInsuranceCompany} from '~/types/Rca';
import {translation, useTranslationStore} from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    insuranceCompany: {
      type: Object as () => RcaInsuranceCompany,
      required: true
    },
    offer: {
      type: Array as () => Array<ApiOfferSubArray> | null,
      default: () => null
    },
    isDeducted: {
      type: Boolean as PropType<boolean | null>,
      default: false
    },
    availability: {
      type: [Array, String, Number] as PropType<Array<any> | string | number | null>,
      default: null
    }
  },
  setup(props) {
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});

    const {offer, isDeducted} = toRefs(props);
    const firstOffer = ref(<ApiOfferSubArray>{});
    const secondOffer = ref(<ApiOfferSubArray>{});
    const secondAvailability = 2;

    const updateOffers = (newOffer: Array<ApiOfferSubArray>, isWithDeductedPrice: boolean) => {
      firstOffer.value = newOffer[0].find((o: any) => !!o.is_deducted === isWithDeductedPrice) || {};
      if (newOffer.length === secondAvailability) {
        secondOffer.value = newOffer[1].find((o: any) => !!o.is_deducted === isWithDeductedPrice) || {};
      } else {
        secondOffer.value = {};
      }
    };

    watch(offer, (newOffer) => {
      if (newOffer) updateOffers(newOffer, !!isDeducted.value);
    });

    watch(isDeducted, (newIsDeducted) => {
      updateOffers(<ApiOfferSubArray[]>offer.value, !!newIsDeducted);
    });

    onMounted(async () => {
      if (offer.value) updateOffers(offer.value, !!isDeducted.value);
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
    });

    return {
      translationList,
      translation,
      firstOffer,
      secondOffer
    };
  }
});
</script>

<template>
  <v-card class="d-flex justify-center border-disabled-dark" variant="outlined" rounded="0" min-height="175">
    <v-row class="py-3 px-3 d-flex align-center">
      <v-col cols="6" sm="6" md="3">
        <NuxtImg :src="'/images/companies/insurance/thumbnails/' + insuranceCompany.code + '.webp'"
                 :alt="insuranceCompany.name" class="pa-1 w-100"/>
      </v-col>
      <v-col cols="6" sm="6" md="3" class="text-center">
        {{ translation(translationList.malus_bonus, 'Bonus Malus') }}: {{ firstOffer ? firstOffer.class_bm : '-' }}
      </v-col>
      <v-col cols="6" sm="6" :md="secondOffer ? '3' : '4'" v-if="firstOffer && firstOffer.amount">
        <v-card rounded="0" color="primary-accent" flat link class="cursor-auto">
          <v-card-text>
            <div class="fw-400 fs-16">{{ firstOffer.rca_availability_id }}
              {{ translation(translationList.months, 'Luni') }}: <br/></div>
            <div class="fw-700 fs-24">{{ firstOffer.amount }} RON</div>
            <div class="fw-400 fs-16">{{ translation(translationList.offer_code, 'Cod oferta') }}:
              <span class="fw-700">{{ firstOffer.offer_code }}</span> <br/></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col v-if="secondOffer && secondOffer.amount" cols="6" sm="6" md="3">
        <v-card rounded="0" color="primary-accent" flat link class="cursor-auto">
          <v-card-text>
            <div class="fw-400 fs-16">{{ secondOffer.rca_availability_id }}
              {{ translation(translationList.months, 'Luni') }}: <br/></div>
            <div class="fw-700 fs-24">{{ secondOffer.amount }} RON</div>
            <div class="fw-400 fs-16">{{ translation(translationList.offer_code, 'Cod oferta') }}:
              <span class="fw-700">{{ secondOffer.offer_code }}</span> <br/></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped></style>
